/**
 * The CDN cost page's headline — SYSTEM state, not window emptiness.
 *
 * QC e-112: after run #2521 completed on qa, the default 30-day view still read
 * "Ingesting — first batch in progress" while the very same sentence said data was
 * ingested through 2026-07-23. The old guard was `ingested && daily.length === 0`,
 * which is a statement about the SELECTED WINDOW: the backfill ingests oldest-first,
 * so with an 857 h backlog the last 30 days are legitimately empty long after every
 * run has finished. Window emptiness cannot tell "a run is loading data right now"
 * from "no run is running and this window simply has not been reached yet".
 *
 * So the state is derived from two independent facts:
 *   - is a run in flight?  → `jobRuns(jobName: "cdn-cost", limit: 1).status === 'running'`
 *   - is there a watermark? → `cdnCostSummary.ingestedThrough`
 * and only then from the window's row count.
 *
 * Kept as a pure function so the four states are testable without a browser and
 * without a run: `apps/web/scripts/check-cdn-headline.mjs`.
 */

export type CdnHeadlineState =
  /** No watermark at all — the job has never landed a byte. */
  | 'not-ingested'
  /** A run is in flight and this window has nothing yet. */
  | 'ingesting'
  /** No run in flight, data exists, but this window has not been reached. */
  | 'window-empty'
  /** Rows in the window — the ordinary freshness line (AC-10). */
  | 'ok';

export interface CdnHeadlineInput {
  /** `cdnCostSummary.ingestedThrough` — a PER-FILE watermark, null until the first file commits. */
  ingestedThrough: string | null;
  /** Rows returned by `cdnDailyEgress` for the selected window. */
  dailyRowCount: number;
  /** The selected window, in days. */
  days: number;
  /** Newest `cdn-cost` job run is `running`. */
  runInFlight: boolean;
  /** Injectable clock — the tests pin it. */
  now?: number;
}

export interface CdnHeadline {
  state: CdnHeadlineState;
  /** The sentence to render. Never contains a measured-looking zero. */
  text: string;
  /**
   * When set, the page renders a link after `text` reading "widen to <n> days" that
   * sets `?days=<n>` — the escape hatch out of an empty window, as URL state.
   */
  widenTo?: number;
}

/** The widest window the page offers; the only useful thing to widen to. */
const WIDEST_DAYS = 90;
/** Past this, the watermark is called out as behind (AC-10). */
const STALE_HOURS = 6;

/**
 * Is a `cdn-cost` run in flight? Read over the LAST FEW runs, not the newest one.
 *
 * Review F-5: the newest row is not necessarily the running one. A tick that cannot
 * take the singleton lock INSERTs a `skipped` row (scheduler-runtime.service.ts
 * recordSkipped, `started_at DEFAULT now()`), so a run started at 12:00 and still
 * inside its deadline is hidden behind the 12:15 tick's `skipped` row under
 * `ORDER BY started_at DESC LIMIT 1` — and the page then denies a batch that is
 * genuinely running. Why five rows: the deadline is 25 min (1 500 000 ms, raised in
 * 164f0f4) against 15-minute ticks, so a run overlaps at most two later ticks — at most
 * 2 `skipped` rows precede the `running` one, 3 rows inside a window of 5, two rows
 * of margin. Re-derive this if either the deadline or the cron changes.
 * A stale `running` row is still bounded by the reaper.
 */
export function anyRunInFlight(runs: readonly { status: string }[]): boolean {
  return runs.some((r) => r.status === 'running');
}

/** Age of the newest ingested window, in whole hours. */
function hoursSince(iso: string | null, now: number = Date.now()): number | null {
  if (!iso) return null;
  const t = Date.parse(iso);
  return Number.isNaN(t) ? null : Math.floor((now - t) / 3_600_000);
}

/** `2026-07-23T09:55:12.000Z` → `2026-07-23 09:55`. */
function stamp(iso: string): string {
  return iso.replace('T', ' ').slice(0, 16);
}

export function cdnHeadline(input: CdnHeadlineInput): CdnHeadline {
  const { ingestedThrough, dailyRowCount, days, runInFlight } = input;
  const now = input.now ?? Date.now();

  if (!ingestedThrough) {
    // Deliberately unchanged from the shipped copy — AC-9's empty state reads this.
    return { state: 'not-ingested', text: 'Nothing ingested yet.' };
  }

  const behind = hoursSince(ingestedThrough, now);
  const stale = behind !== null && behind > STALE_HOURS;

  // AC-10, verbatim: the freshness line reads the same in every state that has rows.
  const freshness =
    `Ingested through ${stamp(ingestedThrough)} UTC` +
    (stale ? ` — ${behind}h behind. Check the cdn-cost job on /scheduler.` : '');

  if (dailyRowCount > 0) return { state: 'ok', text: freshness };

  if (runInFlight) {
    return {
      state: 'ingesting',
      text: `Ingesting — batch in progress; daily totals appear when the run finishes. ${freshness}`,
    };
  }

  // No run, a watermark, and nothing in this window: the honest sentence names the
  // window as the reason and offers the way out. It must NOT claim a batch is running.
  const suffix = stale ? ` (${behind}h behind)` : '';
  if (days < WIDEST_DAYS) {
    return {
      state: 'window-empty',
      text: `No traffic days in the last ${days} days yet — ingested through ${stamp(ingestedThrough)} UTC${suffix};`,
      widenTo: WIDEST_DAYS,
    };
  }
  return {
    state: 'window-empty',
    text: `No traffic days in the last ${days} days yet — ingested through ${stamp(ingestedThrough)} UTC${suffix}. Check the cdn-cost job on /scheduler.`,
  };
}
